import { useState } from 'react';
import { StylishNewButton } from 'components/DesignSystems/New/StylishNewButton';
import { SharedIcon } from 'components/SharedIcon/SharedIcon';
import { useCurrentGroup } from 'components/IAP/hooks/commonHooks';
import { useUscgMapContext } from './UscgMapContext';

/**
 * Checks ArcGIS OAuth session via IdentityManager.
 *
 * If session exists show "ArcGIS Connected" disabled button - this means all
 * default external layers should be visible.
 *
 * If no session exists show "Connect ArcGIS" button - clicking this button will
 * trigger OAuth flow.
 */
export const ArcGisConnectButton: React.FC = () => {
  const group = useCurrentGroup();
  const { isArcGISAuthorized, isCheckingArcGISAuthorization, authorizeArcGIS } =
    useUscgMapContext();
  const [isLoading, setIsLoading] = useState(false);
  const isOauthClientConfigured = !!group?.oauth_client_id;

  const handleConnect = async () => {
    if (isArcGISAuthorized) return;

    setIsLoading(true);
    try {
      await authorizeArcGIS();
    } catch {
      // keep button in connect state when authorization fails
    } finally {
      setIsLoading(false);
    }
  };

  const disabled =
    !isOauthClientConfigured ||
    isArcGISAuthorized ||
    isLoading ||
    isCheckingArcGISAuthorization;

  return (
    <StylishNewButton
      className="button--secondary"
      disabled={disabled}
      onClick={handleConnect}
    >
      {!isOauthClientConfigured ? (
        <span>OAuth Client ID not set</span>
      ) : isArcGISAuthorized ? (
        <>
          <span className="pe-1">ArcGIS Connected</span>
          <SharedIcon iconName="check" size="sm" color="success" />
        </>
      ) : isCheckingArcGISAuthorization ? (
        <span>Checking ArcGIS...</span>
      ) : isLoading ? (
        <span>Connecting...</span>
      ) : (
        <span>Connect ArcGIS</span>
      )}
    </StylishNewButton>
  );
};
